import React, { useContext, useEffect, useState } from 'react';
import { Web3Context } from '../../context/Web3Context';
import useContract from '../../hooks/useContract';
import { formatUSDT, formatDate, getActivityLabel, getActivityColor } from '../../utils/formatters';
import { PACKAGE_NAMES } from '../../utils/constants';
import Loader from '../../components/Loader/Loader';
import './Dashboard.css';

const Dashboard = () => {
  const { userId, account } = useContext(Web3Context);
  const { getUserDetails, getUserPackages, getIncomeBreakdown, claimIncome, claimAllPoolIncomes, getUserActivities, getMatrixDetails, loading } = useContract();
  const [userDetails, setUserDetails] = useState(null);
  const [packages, setPackages] = useState([]);
  const [income, setIncome] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [actPage, setActPage] = useState(1);
  const [teamSize, setTeamSize] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setPageLoading(true);
      try {
        const [details, pkgs, inc, acts] = await Promise.all([
          getUserDetails(),
          getUserPackages(),
          getIncomeBreakdown(),
          getUserActivities(),
        ]);
        setUserDetails(details);
        setPackages(pkgs);
        setIncome(inc);
        setActivities(acts.sort((a, b) => b.timestamp - a.timestamp));

        // Get total team from first active package matrix
        const firstActive = pkgs.findIndex((p) => p);
        if (firstActive >= 0) {
          try {
            const matrix = await getMatrixDetails(null, firstActive + 1);
            setTeamSize(matrix.totalTeamSize);
          } catch {}
        }
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  if (pageLoading) return <Loader text="Loading Dashboard" />;

  const activeCount = packages.filter((p) => p).length;
  const highestPkg = packages.reduce((max, active, i) => (active ? i : max), -1);
  const highestPkgName = highestPkg >= 0 ? PACKAGE_NAMES[highestPkg] : 'None';

  const incomeItems = [
    { label: 'Sponsor', value: income?.sponsorIncome, color: '#00E5FF' },
    { label: 'Level', value: income?.levelIncome, color: '#00C9A7' },
    { label: 'Gold Pool', value: income?.goldPoolIncome, color: '#FFD700' },
    { label: 'Platinum Pool', value: income?.platinumPoolIncome, color: '#E5E4E2' },
    { label: 'Diamond Pool', value: income?.diamondPoolIncome, color: '#B9F2FF' },
    { label: 'Crown Pool', value: income?.crownPoolIncome, color: '#FF6B35' },
    { label: 'Blue Pool', value: income?.bluePoolIncome, color: '#3B82F6' },
    { label: 'Ambassador', value: income?.ambassadorPoolIncome, color: '#7B61FF' },
  ];

  const actPerPage = 10;
  const displayActivities = showAllActivities ? activities : activities.slice(0, 5);
  const totalActPages = Math.ceil(activities.length / actPerPage);
  const paginatedActivities = activities.slice((actPage - 1) * actPerPage, actPage * actPerPage);

  return (
    <div className="dashboard-page">
      {/* User Profile Section */}
      <div className="user-profile-card">
        <div className="profile-avatar">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="profile-info">
          <div className="profile-id">ID: {userId}</div>
          <div className="profile-meta">
            <span>Upline: {userDetails?.referrer || '-'}</span>
            <span className="profile-sep">|</span>
            <span className="profile-pkg">{highestPkgName} Package</span>
          </div>
        </div>
        <div className="profile-packages-count">
          <span className="ppc-num">{activeCount}</span>
          <span className="ppc-label">/6 Active</span>
        </div>
      </div>

      {/* Total Income - Highlighted */}
      <div className="total-income-card">
        <span className="tic-label">Total Income</span>
        <span className="tic-value">{formatUSDT(income?.totalIncome || '0')}</span>
        <span className="tic-unit">USDT</span>
      </div>

      {/* Claimable + Claimed */}
      <div className="claim-row">
        <div className="claim-card claimable">
          <div className="cc-top">
            <span className="cc-label">Available to Claim</span>
            <span className="cc-value">{formatUSDT(income?.claimableAmount || '0')} USDT</span>
          </div>
          <button className="btn-claim" onClick={claimAllPoolIncomes} disabled={loading}>
            {loading ? 'Processing...' : 'Claim All'}
          </button>
        </div>
        <div className="claim-card claimed">
          <span className="cc-label">Total Claimed</span>
          <span className="cc-value">{formatUSDT(income?.totalClaimed || '0')} USDT</span>
        </div>
      </div>

      {/* Referral Link */}
      <div className="dash-card referral-section">
        <span className="ref-label">Referral Link</span>
        <div className="ref-box">
          <input type="text" readOnly value={`${window.location.origin}/register?ref=${userId}`} className="ref-input" />
          <button className="btn-copy" onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/register?ref=${userId}`);
            const btn = document.querySelector('.dashboard-page .btn-copy');
            if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { if (btn) btn.textContent = 'Copy'; }, 2000); }
          }}>Copy</button>
        </div>
      </div>

      {/* Team Stats */}
      <div className="team-stats">
        <div className="ts-item">
          <span className="ts-value">{userDetails?.totalDirectReferrals || 0}</span>
          <span className="ts-label">Direct Referrals</span>
        </div>
        <div className="ts-item">
          <span className="ts-value">{teamSize}</span>
          <span className="ts-label">Total Team</span>
        </div>
      </div>

      {/* Income Breakdown */}
      <div className="dash-card">
        <h3 className="card-title">Income Breakdown</h3>
        <div className="breakdown-list">
          {incomeItems.map((item, i) => (
            <div key={i} className="bd-item">
              <div className="bd-dot" style={{ background: item.color }}></div>
              <span className="bd-label">{item.label}</span>
              <span className="bd-value" style={{ color: item.color }}>{formatUSDT(item.value || '0')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Activities */}
      <div className="dash-card">
        <div className="card-header-row">
          <h3 className="card-title">Recent Activities</h3>
          {!showAllActivities && activities.length > 5 && (
            <button className="view-all-btn" onClick={() => setShowAllActivities(true)}>View All</button>
          )}
          {showAllActivities && (
            <button className="view-all-btn" onClick={() => { setShowAllActivities(false); setActPage(1); }}>Hide</button>
          )}
        </div>
        <div className="activity-list">
          {(showAllActivities ? paginatedActivities : displayActivities).map((act) => (
            <div key={act.id} className="act-item">
              <div className="act-dot" style={{ background: getActivityColor(act.activityType) }}></div>
              <div className="act-info">
                <span className="act-type" style={{ color: getActivityColor(act.activityType) }}>{getActivityLabel(act.activityType)}</span>
                <span className="act-time">{formatDate(act.timestamp)}</span>
              </div>
              <span className="act-amount">{parseFloat(act.amount) > 0 ? `${formatUSDT(act.amount)}` : '-'}</span>
            </div>
          ))}
          {activities.length === 0 && <p className="empty-msg">No activities yet</p>}
        </div>
        {showAllActivities && totalActPages > 1 && (
          <div className="pagination">
            <button className="pg-btn" disabled={actPage <= 1} onClick={() => setActPage(actPage - 1)}>Prev</button>
            <span className="pg-info">{actPage} / {totalActPages}</span>
            <button className="pg-btn" disabled={actPage >= totalActPages} onClick={() => setActPage(actPage + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
